import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";

const schema = z.object({
  name: z.string().min(1, "Nazwa jest wymagana"),
  contactEmail: z
    .string()
    .email("Nieprawidłowy email")
    .or(z.literal(""))
    .optional(),
});

type FormData = z.infer<typeof schema>;

export type OrgItem = {
  id: string;
  name: string;
  contactEmail: string | null;
  memberCount: number;
  createdAt: string;
};

export function OrgEditDialog({
  open,
  onOpenChange,
  org,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  org?: OrgItem | null;
}) {
  const utils = trpc.useUtils();
  const isEdit = !!org;

  const createMutation = trpc.admin.createAdminOrg.useMutation({
    onSuccess: () => {
      utils.admin.listAdminOrgs.invalidate();
      toast.success("Organizacja utworzona");
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.admin.updateAdminOrg.useMutation({
    onSuccess: () => {
      utils.admin.listAdminOrgs.invalidate();
      toast.success("Organizacja zaktualizowana");
      onOpenChange(false);
    },
    onError: (err) => toast.error(err.message),
  });

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", contactEmail: "" },
  });

  useEffect(() => {
    if (open) {
      if (org) {
        form.reset({
          name: org.name,
          contactEmail: org.contactEmail ?? "",
        });
      } else {
        form.reset({ name: "", contactEmail: "" });
      }
    }
  }, [open, org, form]);

  const onSubmit = form.handleSubmit((data) => {
    if (isEdit && org) {
      updateMutation.mutate({
        orgId: org.id,
        name: data.name,
        contactEmail: data.contactEmail || null,
      });
    } else {
      createMutation.mutate({
        name: data.name,
        contactEmail: data.contactEmail || null,
      });
    }
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edytuj organizację" : "Nowa organizacja"}
          </DialogTitle>
        </DialogHeader>
        <DialogForm onSubmit={onSubmit}>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="o-name">Nazwa</Label>
              <Input id="o-name" {...form.register("name")} />
              {form.formState.errors.name && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="o-email">Email kontaktowy</Label>
              <Input
                id="o-email"
                type="email"
                {...form.register("contactEmail")}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Anuluj
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? "Zapisywanie..."
                : isEdit
                  ? "Zapisz zmiany"
                  : "Utwórz"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
